import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Building2, Handshake, ArrowLeftRight, Heart, Landmark, Scale, ShieldCheck, Users, Copyright, FileCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { servicesData } from '../data/services';

const iconMap = {
  FileText,
  Building2,
  Handshake,
  ArrowLeftRight,
  Heart,
  Landmark,
  Scale,
  ShieldCheck,
  Users,
  Copyright,
  FileCheck,
};

const ServicesOverview = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const scrollRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, servicesData.length - itemsPerView);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [maxIndex]);

  return (
    <section id="services" className="py-16 md:py-20 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#2C3E50] mb-4">
            Our Services
          </div>
          <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">
            Comprehensive Legal Solutions
          </h2>
          <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">
            From corporate transactions to intellectual property protection, we offer full-spectrum legal services.
          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden">
            <div
              ref={scrollRef}
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
            >
              {servicesData.map((service) => {
                const Icon = iconMap[service.icon];
                return (
                  <div
                    key={service.id}
                    className="flex-shrink-0 px-4"
                    style={{ width: `${100 / itemsPerView}%` }}
                  >
                    <Link
                      to={service.path}
                      className="block bg-white border border-[#2C3E50]/5 shadow-sm hover:shadow-md transition-all duration-300 p-6 h-full group hover:-translate-y-1"
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="mb-4">
                        <Icon className="w-10 h-10 text-[#CA9A67]" strokeWidth={1.5} />
                      </div>
                      <h4 className="font-semibold text-lg text-[#2C3E50] mb-2 group-hover:text-[#1A252F]">
                        {service.title}
                      </h4>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">
                        {service.shortDesc}
                      </p>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white border border-[#2C3E50]/10 p-3 hover:bg-[#2C3E50] hover:text-white transition-colors shadow-lg"
            data-testid="services-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white border border-[#2C3E50]/10 p-3 hover:bg-[#2C3E50] hover:text-white transition-colors shadow-lg"
            data-testid="services-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: maxIndex + 1 }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentIndex === index ? 'bg-[#CA9A67] w-6' : 'bg-[#CA9A67]/30'
                }`}
                data-testid={`service-dot-${index}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;
